import Vue from 'vue';
import { clone, cloneDeep, findIndex } from 'lodash';
import api from 'src/api';

// в константы выносим только названия мутаций,
// которые переиспользуем/планируем переиспользовать
export const ADD_MARKER = 'ADD_MARKER';
export const SET_FETCHING = 'SET_FETCHING';
export const SET_STOP_FETCHING = 'SET_STOP_FETCHING';
export const SYNC = 'SYNC';
export const REMOVE_MARKER = 'REMOVE_MARKER';

const data = {
  directories: [],
  fetching: false,
  directoriesTotal: 0,
  pageSizeDefault: undefined,
};

const actions = {
  fetchDirectories({ commit }) {
    commit(SET_FETCHING);
    return api.getDirectories()
      .then((res) => commit(SYNC, { directories: res }))
      .catch((err) => {
        commit(SET_STOP_FETCHING);
        throw err;
      });
  },
  fetchDirectoriesPagination({ commit }, { page, pageSize, moreData = false }) {
    commit(SET_FETCHING);
    return api.getDirectoriesPagination(page, pageSize)
      .then(({
        directories, totalCount, pageSize: pageSizeDefault,
      }) => commit(SYNC, {
        directories, totalCount, moreData, pageSizeDefault,
      }))
      .catch((err) => {
        commit(SET_STOP_FETCHING);
        throw err;
      });
  },
  removeDirectory({ commit }, id) {
    commit(SET_FETCHING, id);
    return api.deleteDirectory(id)
      .then(() => commit('REMOVE_DIRECTORY', id))
      .catch((err) => {
        commit(SET_STOP_FETCHING);
        throw err;
      });
  },
  removeMarker({ commit }, { id, directory }) {
    return api.deleteMarker(id)
      .then(() => commit(REMOVE_MARKER, { id, directory }));
  },
  createDirectory({ commit }, model) {
    return api.createDirectory(model).then((directory) => {
      commit('ADD_DIRECTORY', directory);
    });
  },
  createMarker({ commit }, model) {
    return api.createMarker(model).then((marker) => {
      commit(ADD_MARKER, marker);
    });
  },
  updateDirectory({ commit }, { id, model }) {
    return api.updateDirectory(id, model).then((directory) => {
      commit('UPDATE_DIRECTORY', directory);
    });
  },
  updateMarker({ commit }, { id, model, oldDirectoryId }) {
    return api.updateMarker(id, model).then((marker) => {
      if (model.directoryId !== oldDirectoryId) {
        commit(ADD_MARKER, marker);
        commit(REMOVE_MARKER, { id, directory: oldDirectoryId });
      }
      commit('UPDATE_MARKER', marker);
    });
  },
};

const mutations = {
  ADD_DIRECTORY(state, directory) {
    state.directories.push(clone(directory));
  },
  ADD_MARKER(state, marker) {
    const { directories } = state;
    const index = findIndex(directories, { id: marker.directory.id });
    const directory = cloneDeep(directories[index]);
    directory.markers.push(marker);
    Vue.set(directories, index, directory);
  },
  SET_FETCHING(state) {
    state.fetching = true;
  },
  SET_STOP_FETCHING(state) {
    state.fetching = false;
  },
  SYNC(state, {
    directories, totalCount, moreData, pageSizeDefault,
  }) {
    state.pageSizeDefault = !totalCount ? null : pageSizeDefault || state.pageSizeDefault;
    if (moreData) {
      state.directories = state.directories.concat(cloneDeep(directories));
    } else {
      state.directories = cloneDeep(directories);
    }
    state.directoriesTotal = totalCount || state.directoriesTotal;
    mutations.SET_STOP_FETCHING(state);
  },
  REMOVE_DIRECTORY(state, id) {
    const { directories } = state;
    Vue.delete(directories, findIndex(directories, { id }));
    state.directoriesTotal -= 1;
  },
  REMOVE_MARKER(state, { id, directory: directoryId }) {
    const { directories } = state;
    const indexDirectory = findIndex(directories, { id: directoryId });
    const directory = cloneDeep(directories[indexDirectory]);
    const { markers } = directory;
    const markerIndex = findIndex(markers, { id });
    markers.splice(markerIndex, 1);
    Vue.set(directories, indexDirectory, directory);
  },
  UPDATE_DIRECTORY(state, directory) {
    const { id } = directory;
    const { directories } = state;
    const index = findIndex(directories, { id });
    Vue.set(directories, index, { ...directories[index], ...directory });
  },
  UPDATE_MARKER(state, marker) {
    const { directories } = state;
    const index = findIndex(directories, { id: marker.directory.id });
    const directory = cloneDeep(directories[index]);
    const { markers } = directory;
    const markerIndex = findIndex(markers, { id: marker.id });
    Object.assign(markers[markerIndex], marker);
    Vue.set(directories, index, directory);
  },
};

const getters = {
  getDirectoryById(state) {
    const { directories } = state;
    return (id) => (directories[findIndex(directories, { id })]);
  },
  getMarkerById(state) {
    const { directories } = state;
    const markers = [];
    directories.forEach((directory) => {
      directory.markers.forEach((item) => {
        const marker = clone(item);
        marker.directory = directory.id;
        marker.directory = {
          id: directory.id,
        };
        markers.push(marker);
      });
    });
    return (id) => (markers[findIndex(markers, { id })]);
  },
};

const store = {
  namespaced: true,
  state: data,
  mutations,
  getters,
  actions,
};

export default store;
